import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import {
	MESSAGE_LIST_GENERIC_ACTIVITY_LIST_SORT,
	MESSAGE_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/messageList/localStorageKeys';
import {
	GetTheMessageModuleBoxesForTheUserQuery,
	GetTheMessageModuleMessagesForTheUserQuery,
} from 'src/stores/messageList/queries';
import createMessageListState from 'src/stores/messageList/state';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { MessageListItem, MessageListItemRunType } from 'src/models/messageList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import { setTheStateForTheAggregateIds } from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterMessageListCallBack,
	GetTheAllMessageModuleMessagesForTheUserCallBack,
	GetTheMessageModuleBoxesForTheUserQueryCallBack,
	GetTheMineMessageModuleMessagesForTheUserCallBack,
	GetTheOpenMessageModuleMessagesForTheUserCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	ListFetchPayload,
	QueryStorePayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetDirectionFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'messageList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'messageList';

const defineMessageListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheMessageModuleMessagesForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheMessageModuleMessagesForTheUserQuery.fromJSON<GetTheMessageModuleMessagesForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			async fetchAllMessageList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllMessageList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllMessageList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllMessageModuleMessagesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MessageListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MessageListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheMessageModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheMessageModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterMessageListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_MESSAGE),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineMessageList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineMessageList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineMessageList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineMessageModuleMessagesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MessageListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MessageListItemRunType,
				});
			},
			async fetchOpenMessageList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenMessageList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenMessageList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenMessageModuleMessagesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MessageListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MessageListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<MessageListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.messageListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.messageListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.messageListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheMessageModuleMessagesForTheUserQuery(
						completeListExportViewFor(payload.activityListType),
						resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						this.filteredBoxIds,
						this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						payload.searchTerm,
						payload.hiddenSearchTerm,
						this.directionFilter,
						pagination,
						this.sortFor(payload.activityListType),
						suppressBoxCustomCouplingForInstance(this.$id)
					)
				);

				let completeList: MessageListItem[] | null = null;

				await payload.queryStore.query<MessageListItem[]>({
					callback: new ResolveListForExportCallback<MessageListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MessageListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllMessageList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.ALL),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMessageListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineMessageList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.MINE),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMessageListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenMessageList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheMessageModuleMessagesForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.OPEN),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMessageListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			replaceMessageListQueryStoreSubscription(payload: {
				query: GetTheMessageModuleMessagesForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheMessageModuleMessagesForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheMessageModuleMessagesForTheUserQuery.fromJSON<GetTheMessageModuleMessagesForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheMessageModuleMessagesForTheUserQuery.fromJSON<GetTheMessageModuleMessagesForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.directionFilter = 'combined';
				this.selectedSharedQueryId = '';
				this.queryStoreSubscriptions = [];
				this.messageListOpenTotalCount = 0;
				this.messageListOpen = [];
				this.messageListOpenSliced.clear();
				this.messageListAllTotalCount = 0;
				this.messageListAll = [];
				this.messageListAllSliced.clear();
				this.messageListMineTotalCount = 0;
				this.messageListMine = [];
				this.messageListMineSliced.clear();
				this.pagination = createDefaultPaginationMap();
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllMessageList(payload: SetListPayload): void {
				this.messageListAllTotalCount = payload.totalCount;
				this.messageListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.messageListAll = dedupByAggregateId(
					[...this.messageListAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MessageListItem>(sort.property as keyof MessageListItem, sort.direction));
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setDirectionFilterAction(payload: SetDirectionFilterPayload): void {
				this.setDirectionFilterValue(payload.directionFilter);
			},
			setDirectionFilterValue(payload: 'combined' | 'in' | 'out'): void {
				this.directionFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'messageList') {
					localStorage.setItem(MESSAGE_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineMessageList(payload: SetListPayload): void {
				this.messageListMineTotalCount = payload.totalCount;
				this.messageListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.messageListMine = dedupByAggregateId(
					[...this.messageListMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MessageListItem>(sort.property as keyof MessageListItem, sort.direction));
			},
			setOpenMessageList(payload: SetListPayload): void {
				this.messageListOpenTotalCount = payload.totalCount;
				this.messageListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.messageListOpen = dedupByAggregateId(
					[...this.messageListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MessageListItem>(sort.property as keyof MessageListItem, sort.direction));
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'messageList') {
					localStorage.setItem(MESSAGE_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setTheMessageStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheMessageStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheMessageStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.messageListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.messageListAll.find((item) => item.aggregateId === aggregateId) ??
						this.messageListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.messageListOpenTotalCount = Math.max(0, this.messageListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<MessageListItem>(
					setTheStateForTheAggregateIds(
						this.messageListOpen as never[],
						aggregateIds,
						status
					) as unknown as MessageListItem[],
					this.messageListOpenTotalCount,
					(payload) => this.setOpenMessageList(payload)
				);
				setPagedList<MessageListItem>(
					setTheStateForTheAggregateIds(
						this.messageListAll as never[],
						aggregateIds,
						status
					) as unknown as MessageListItem[],
					this.messageListAllTotalCount,
					(payload) => this.setAllMessageList(payload)
				);
				setPagedList<MessageListItem>(
					setTheStateForTheAggregateIds(
						this.messageListMine as never[],
						aggregateIds,
						status
					) as unknown as MessageListItem[],
					this.messageListMineTotalCount,
					(payload) => this.setMineMessageList(payload)
				);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllMessageList(): void {
				this.messageListAllTotalCount = 0;
				this.messageListAll = [];
				this.messageListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineMessageList(): void {
				this.messageListMineTotalCount = 0;
				this.messageListMine = [];
				this.messageListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenMessageList(): void {
				this.messageListOpenTotalCount = 0;
				this.messageListOpen = [];
				this.messageListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createMessageListState,
	});

export const useMessageListStore = defineMessageListStore('messageList');

const messageListInstanceDefinitions = new Map<string, ReturnType<typeof defineMessageListStore>>();

export const createMessageListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useMessageListStore();
	}

	const storeId = deriveListStoreId('messageList', instanceId);
	let definition = messageListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineMessageListStore(storeId);
		messageListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
